import { useState } from "react";
import { WifiOff, Users, Bot, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ContactModal } from "@/components/ContactModal";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};

const tools = [
  {
    icon: <Bot className="w-5 h-5" />,
    barrier: "Students need help but there's no one there to give it",
    tool: "Koda",
    solve: "An AI coding tutor available anytime, explains concepts in plain language with real-world analogies, works across topics.",
    support: "Warm, encouraging, built for kids ages 8 to 14.",
    link: "https://koda.bytesizedpython.org"
  },
  {
    icon: <WifiOff className="w-5 h-5" />,
    barrier: "No reliable internet in the classroom",
    tool: "Koda Studio",
    solve: "A browser-based Python IDE built on Pyodide that runs entirely client-side, optimized for Chromebooks and low-broadband schools.",
    support: "Two modes: Playground for free exploration, Curriculum for structured topic-based exercises with built-in hints.",
    link: "https://studio.bytesizedpython.org"
  },
  {
    icon: <Users className="w-5 h-5" />,
    barrier: "No Python-expert volunteers to run a session",
    tool: "Koda Live",
    solve: "Any adult can facilitate. Koda runs the lesson; the facilitator just watches a color-coded student dashboard and walks toward the student who needs help.",
    support: "Removes the single biggest bottleneck to scaling in-person coding sessions.",
    contactEmail: null,
    link: "https://live.bytesizedpython.org"
  }
];

export const KodaSuiteSection = ({ studentNumber, schoolPartners, classesNumber }: { studentNumber: number; schoolPartners: number; classesNumber: number }) => {
  const [contactModalOpen, setContactModalOpen] = useState(false);

  return (
    <section className="relative pt-24 pb-12 overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="max-w-5xl mx-auto text-center space-y-16"
        >
          {/* Header */}
          <motion.div variants={item} className="space-y-6">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-foreground">
              The <span className="text-primary">Koda</span> Suite
            </h2>
            <motion.div
              variants={item}
              className="h-0.5 bg-gradient-to-r from-primary/30 to-transparent w-24 mx-auto"
            />
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              Free tools built for the constraints under-resourced schools actually face.
            </p>
          </motion.div>

          {/* Tool Blocks */}
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 items-start">
            {tools.map((tool) => (
              <motion.div
                key={tool.tool}
                variants={item}
                className="group bg-white/90 backdrop-blur-sm rounded-xl p-8 border border-border shadow-card hover:shadow-hero transition-all flex flex-col text-left h-full min-h-[550px]"
              >
                <div className="flex items-center justify-center bg-secondary/30 h-14 w-14 rounded-full text-foreground mb-6">
                  {tool.icon}
                </div>

                <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">
                  The barrier
                </p>
                <h3 className="text-xl font-semibold text-foreground leading-snug mb-4">
                  "{tool.barrier}"
                </h3>

                <span className="inline-block w-fit bg-secondary/30 text-foreground text-sm font-semibold rounded-full px-4 py-1 mb-4">
                  {tool.tool}
                </span>

                <p className="text-muted-foreground leading-relaxed mb-4">
                  {tool.solve}
                </p>

                <p className="text-sm text-muted-foreground/80 border-t border-border pt-4 mt-auto">
                  {tool.support}
                </p>
                {tool.contactEmail ? (
                  <Button
                    variant="outline"
                    className="w-full mt-4 bg-primary text-primary-foreground border-primary hover:bg-white hover:text-foreground hover:border-border transition-colors"
                    onClick={() => setContactModalOpen(true)}
                  >
                    Contact Us
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <a
                    href={tool.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4"
                  >
                    <Button variant="outline" className="w-full bg-primary text-primary-foreground border-primary hover:bg-white hover:text-foreground hover:border-border transition-colors">
                      Visit {tool.tool}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </a>
                )}
              </motion.div>
            ))}
          </div>

          {/* Stats Section */}
          <motion.div
            variants={item}
            className="grid grid-cols-2 md:grid-cols-3 gap-8 pt-4"
          >
            <div className="text-center space-y-2">
              <div className="text-3xl font-bold text-primary">{studentNumber}+</div>
              <div className="text-sm text-muted-foreground">Students Reached with Koda Studio</div>
            </div>
            <div className="text-center space-y-2">
              <div className="text-3xl font-bold text-primary">{schoolPartners}</div>
              <div className="text-sm text-muted-foreground">School Partners</div>
            </div>
            <div className="text-center space-y-2">
              <div className="text-3xl font-bold text-primary">{classesNumber}</div>
              <div className="text-sm text-muted-foreground">Classes Delivered with Partners</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
      <ContactModal isOpen={contactModalOpen} onClose={() => setContactModalOpen(false)} />
    </section>
  );
};
